// actions/stripeActions.ts
"use server";

import { auth, currentUser } from "@clerk/nextjs/server";
import { absoluteUrl } from "@/lib/utils";
import { stripe } from "@/lib/stripe"; // Assuming you have lib/stripe.ts configured
import { fetchQuery } from "convex/nextjs";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { APP_SUBDOMAIN } from "@/lib/constants/constants";

type ActionResponse = {
  data?: { url: string };
  error?: { code: string; message: string };
};

type PortalResponse = {
  data?: { url: string };
  error?: { code: string; message: string };
};

type UpdateResponse = {
  data?: { success: boolean };
  error?: { code: string; message: string };
};

export const createCheckoutSession = async (
  priceId: string,
  planLookupKey: string, // Identify the plan being purchased
  isYearly: boolean = false,
): Promise<ActionResponse> => {
  const authData = await auth();
  const { userId, orgId } = authData; // Get current org context if possible
  const user = await currentUser();
  const token = (await authData.getToken({ template: "convex" })) ?? undefined;

  if (!userId || !user?.primaryEmailAddress?.emailAddress) {
    // If user isn't logged in, redirect them to sign-in before checkout?
    // Or allow checkout and link later? For SaaS, usually require login.
    return {
      error: {
        code: "UNAUTHORIZED",
        message: "User must be logged in to subscribe.",
      },
    };
  }

  if (!orgId) {
    // If user has no active organization selected in Clerk
    return {
      error: {
        code: "NO_ORG",
        message: "Please select or create an organization before subscribing.",
      },
    };
  }

  // Get organization details to check for existing Stripe Customer ID
  let stripeCustomerId: string | undefined | null = undefined;
  let fetchedPriceId: string | null = null;

  try {
    // Fetch Plan details from Convex using lookupKey to get the correct Price ID
    const planResult = await fetchQuery(
      api.plans.getPlanByLookupKey,
      { lookupKey: planLookupKey },
      { token }, // Assuming query requires token if accessing sensitive plan details
    );

    if (planResult.error || !planResult.data) {
      return {
        error: {
          code: "PLAN_NOT_FOUND",
          message: planResult.error?.message || "Selected plan not found.",
        },
      };
    }

    fetchedPriceId = planResult.data.stripePriceId; // Get Price ID from the plan
    if (!fetchedPriceId) {
      return {
        error: {
          code: "PRICE_ID_MISSING",
          message: `Stripe Price ID not configured for plan: ${planLookupKey}`,
        },
      };
    }

    const orgDetails = await fetchQuery(
      api.mainSaas.organizations.getOrganizationById, // Assuming you have this query
      { organizationId: orgId },
      { token },
    );
    if (orgDetails?.data?.stripeCustomerId) {
      stripeCustomerId = orgDetails.data.stripeCustomerId;
    }
  } catch (e) {
    console.warn(
      "Could not fetch org details to check for Stripe Customer ID",
      e,
    );
  }

  const successUrl = `${APP_SUBDOMAIN}/projects`; // Redirect back after success
  const cancelUrl = absoluteUrl(`/home/pricing`); // Redirect back to pricing if cancelled

  try {
    console.log("Creating Stripe checkout session...");
    const stripeSession = await stripe.checkout.sessions.create({
      success_url: successUrl,
      cancel_url: cancelUrl,
      payment_method_types: ["card"],
      mode: "subscription",
      billing_address_collection: "auto",
      customer: stripeCustomerId, // Use existing ID if available
      customer_email: stripeCustomerId
        ? undefined
        : user.primaryEmailAddress.emailAddress, // Provide email only if creating new customer
      line_items: [
        {
          price: fetchedPriceId as string,
          quantity: 1,
        },
      ],
      metadata: {
        orgId: orgId, // Pass Convex Org ID
        userId: userId, // Pass Clerk User ID
        planLookupKey: planLookupKey, // Identify the purchased plan
        interval: isYearly ? "year" : "month",
      },
      // Enable trial if applicable for the specific plan (rare for paid plans)
      // subscription_data: {
      //     trial_period_days: planData?.trialPeriodDays || undefined
      // }
    });

    if (!stripeSession.url) {
      return {
        error: {
          code: "STRIPE_ERROR",
          message: "Could not create Stripe checkout session.",
        },
      };
    }

    return { data: { url: stripeSession.url } };
  } catch (error: any) {
    console.error("Stripe Checkout Error:", error);
    return {
      error: {
        code: "STRIPE_ERROR",
        message: `Stripe Error: ${error.message}`,
      },
    };
  }
};

export const createBillingPortalSession = async (): Promise<PortalResponse> => {
  const authData = await auth();
  const { userId, orgId } = authData;
  const token = (await authData.getToken({ template: "convex" })) ?? undefined;

  if (!userId || !orgId || !token) {
    return {
      error: {
        code: "UNAUTHORIZED",
        message: "User not authenticated or organization not found.",
      },
    };
  }

  // 1. Get the Org's Stripe Customer ID from Convex
  let stripeCustomerId: string | null = null;
  try {
    const org = await fetchQuery(
      api.mainSaas.organizations.getOrganizationById, // Assuming this query exists
      { organizationId: orgId as Id<"organizations"> },
      { token },
    );
    stripeCustomerId = org?.data?.stripeCustomerId ?? null;

    if (!stripeCustomerId) {
      return {
        error: {
          code: "NO_STRIPE_CUSTOMER",
          message: "Stripe customer ID not found for this organization.",
        },
      };
    }
  } catch (e: any) {
    console.error("Error fetching organization:", e);
    return {
      error: {
        code: "DB_ERROR",
        message: "Failed to retrieve organization details.",
      },
    };
  }

  // 2. Define the return URL (where user goes after portal)
  const returnUrl = `${APP_SUBDOMAIN}/projects`;

  // 3. Create the Billing Portal Session using Stripe API
  try {
    const portalSession = await stripe.billingPortal.sessions.create({
      customer: stripeCustomerId,
      return_url: returnUrl,
    });

    if (!portalSession.url) {
      return {
        error: {
          code: "STRIPE_ERROR",
          message: "Could not create Stripe billing portal session.",
        },
      };
    }

    return { data: { url: portalSession.url } };
  } catch (error: any) {
    console.error("Stripe Billing Portal Error:", error);
    return {
      error: {
        code: "STRIPE_ERROR",
        message: `Stripe Error: ${error.message}`,
      },
    };
  }
};

// --- NEW: updateSubscriptionPlan ---
export const updateSubscriptionPlan = async (
  newPlanLookupKey: string,
  isNewIntervalYearly: boolean, // To fetch correct new price ID
): Promise<UpdateResponse> => {
  const authData = await auth();
  const { userId, orgId } = authData;
  const token = (await authData.getToken({ template: "convex" })) ?? undefined;

  if (!userId || !orgId || !token) {
    return {
      error: { code: "UNAUTHORIZED", message: "Authentication required." },
    };
  }

  try {
    // 1. Get Current Org/Subscription Details from Convex
    const orgDetails = await fetchQuery(
      api.mainSaas.organizations.getOrganizationById, // Ensure this query returns stripeSubscriptionId
      { organizationId: orgId },
      { token },
    );

    const currentSubscriptionId = orgDetails?.data?.stripeSubscriptionId;
    if (!currentSubscriptionId) {
      return {
        error: {
          code: "NO_SUBSCRIPTION",
          message: "No active Stripe subscription found for this organization.",
        },
      };
    }

    // 2. Get the NEW Stripe Price ID from Convex based on the lookup key
    const newPlanResult = await fetchQuery(
      api.plans.getPlanByLookupKey,
      { lookupKey: newPlanLookupKey },
      { token },
    );
    if (newPlanResult.error || !newPlanResult.data) {
      return {
        error: {
          code: "PLAN_NOT_FOUND",
          message:
            newPlanResult.error?.message || "Target plan details not found.",
        },
      };
    }
    const newPriceId = newPlanResult.data.stripePriceId;
    if (!newPriceId) {
      return {
        error: {
          code: "PRICE_ID_MISSING",
          message: `Stripe Price ID not configured for plan: ${newPlanLookupKey}`,
        },
      };
    }

    // 3. Retrieve the current Stripe Subscription to get the item ID
    const currentSubscription = await stripe.subscriptions.retrieve(
      currentSubscriptionId,
      {
        expand: ["items.data.price.product"], // Expand to potentially check product if needed
      },
    );
    if (!currentSubscription || currentSubscription.items.data.length === 0) {
      return {
        error: {
          code: "STRIPE_ERROR",
          message:
            "Could not retrieve current subscription details from Stripe.",
        },
      };
    }
    const currentSubscriptionItemId = currentSubscription.items.data[0].id; // Get the ID of the item to update

    // 4. Update the Subscription in Stripe
    console.log(
      `Updating Stripe subscription ${currentSubscriptionId} item ${currentSubscriptionItemId} to new price ${newPriceId}`,
    );
    await stripe.subscriptions.update(currentSubscriptionId, {
      items: [
        {
          id: currentSubscriptionItemId,
          price: newPriceId, // Set the new price ID
        },
      ],
      proration_behavior: "create_prorations", // Prorate the cost difference
      metadata: {
        // Optionally update metadata if needed
        orgId: orgId,
        userId: userId,
        planLookupKey: newPlanLookupKey,
        interval: isNewIntervalYearly ? "year" : "month",
      },
      // You can add trial_end: 'now' if you want to end any existing trial immediately on upgrade
    });

    // 5. Success! The webhook handler will update the Convex DB.
    console.log(
      `Stripe subscription ${currentSubscriptionId} update initiated successfully.`,
    );
    return { data: { success: true } };
  } catch (error: any) {
    console.error("Stripe Subscription Update Error:", error);
    // Provide more specific error messages if possible
    let message = `Stripe Error: ${error.message}`;
    if (error.code === "resource_missing") {
      message = "Could not find the subscription to update in Stripe.";
    }
    return { error: { code: "STRIPE_ERROR", message: message } };
  }
};
